import React, { useEffect, useMemo, useState } from "react";
import { Image, Stack } from "react-bootstrap";

import "./PreciseLocationNotFound.scss";
import PrimaryActionButton from "../Buttons/PrimaryActionButton/PrimaryActionButton";
import BottomDrawer from "../BottomDrawer/BottomDrawer";
import {
  FunctionWithVoidReturn,
  GenericModalProps,
  WebApiPermissionStates,
} from "../../utils/constants/generic-types";
import { HandleGetDeviceLocationInfo } from "../topup/TopupFlow/types";
import { useTranslation } from "react-i18next";
import useAnalytics from "../../hooks/useAnalytics";
import { AllowLocationOptionProperty } from "../../utils/analytics";
import ImagePreciseLocationPin from "../../images/precise-location-pin.svg";
import useCountdown from "../../hooks/useCountdown";
import TimeCircleOutlineIcon from "../icons/TimeCircleOutlineIcon";
import LightBulbOutlineIcon from "../icons/LightBulbOutlineIcon";
import Overlay from "../Overlay/Overlay";
import LoadingSpinner from "../Payments/LoadingSpinner/LoadingSpinner";

interface AllowLocationProps extends GenericModalProps {
  openAllowLocationManually: FunctionWithVoidReturn;
  handleDeviceLocationCheck: HandleGetDeviceLocationInfo;
  handleSuccessfulPrelimTopupChecks?: FunctionWithVoidReturn;
}

const RETRY_TIME = 60 * 1000; //1 minute

function VerifyingLocationOverlay() {
  const { t } = useTranslation("common", { keyPrefix: "location" });
  return (
    <Overlay
      style={{ zIndex: 1060 }}
      className="d-flex justify-content-center align-items-center"
    >
      <div>
        <Stack
          gap={3}
          className="justify-content-center align-items-center VerifyingLocationLoader"
        >
          <LoadingSpinner width={48} height={48} />
          <p className="Grotesk SpecialFontSettings VerifyingLocationText">
            {t("verifying_location")}...
          </p>
        </Stack>
      </div>
    </Overlay>
  );
}

export default function PreciseLocationNotFound({
  isOpen,
  onClose,
  openAllowLocationManually,
  handleDeviceLocationCheck,
  handleSuccessfulPrelimTopupChecks,
}: AllowLocationProps) {
  const [loading, setLoading] = useState<boolean>(false);
  const [countdownIsEnabled, setCountdownIsEnabled] = useState<boolean>(false);
  const retryCountdown = useCountdown({
    enabled: countdownIsEnabled,
    futureDateOrTotalTimeInMs: RETRY_TIME,
    showHours: false,
  });
  const { t } = useTranslation("common", { keyPrefix: "location" });
  const { sendAllowLocationOptionsEvent } = useAnalytics();

  const tips = useMemo(
    () => [
      t("retry_after_some_minutes"),
      t("disable_vpn_or_spoofing_apps"),
      t("move_to_an_area_with_better_gps_signal"),
      t("restart_your_device"),
    ],
    [t]
  );

  const handleRetryLocationCheck = () => {
    setLoading(true);
    handleDeviceLocationCheck(
      ({ locationStatus, isLocationPrecise = true, canTopup }) => {
        setTimeout(() => {
          setLoading(false);

          if (locationStatus === WebApiPermissionStates.DENIED) {
            sendAllowLocationOptionsEvent(
              AllowLocationOptionProperty.ACCESS_DENIED
            );
            onClose();
            openAllowLocationManually();
            return;
          }

          if (!isLocationPrecise) {
            setCountdownIsEnabled(true);
            return;
          }

          if (canTopup) {
            handleSuccessfulPrelimTopupChecks?.();
            onClose();
          }
          sendAllowLocationOptionsEvent(
            AllowLocationOptionProperty.ACCESS_GRANTED
          );
        }, 2000);
      }
    );
  };

  useEffect(() => {
    if (!retryCountdown) return;
    const countdownIsAtZero = !/[^0:]/.test(retryCountdown);
    if (countdownIsAtZero) setTimeout(() => setCountdownIsEnabled(false), 1200);
  }, [retryCountdown]);

  return (
    <>
      <BottomDrawer
        isOpen={isOpen}
        onClose={onClose}
        containerClass="PreciseLocationNotFoundModal"
      >
        <div className="ModalContent">
          <Stack gap={3} className="align-items-center justify-content-center">
            <div className="d-flex justify-content-center align-items-center p-4 LocationIconContainer">
              <Image
                src={ImagePreciseLocationPin}
                alt={t("precise_location_not_found")}
                width={72}
                height={72}
              />
            </div>
            <Stack gap={2} className="Grotesk Copy">
              <p className="MainCopy">
                {t("we_could_not_verify_your_exact_location")}
              </p>
              <p className="SubCopy">
                {t(
                  "your_location_seems_outside_your_current_network_area_please_move"
                )}
              </p>
            </Stack>
            <Stack gap={2} className="TipsContainer">
              <Stack direction="horizontal" gap={2}>
                <LightBulbOutlineIcon />
                <p className="Grotesk HelpText">{t("help")}</p>
              </Stack>
              <ul className="Tips">
                {tips.map((tip, index) => (
                  <li key={index} className="Tip">
                    {tip}
                  </li>
                ))}
              </ul>
            </Stack>
          </Stack>

          <Stack gap={2} className="CountdownAndBtnContainer">
            {!!retryCountdown && (
              <Stack
                direction="horizontal"
                gap={1}
                className="justify-content-center"
              >
                <TimeCircleOutlineIcon />
                <p className="Grotesk SpecialFontSettings Countdown">
                  {retryCountdown}
                </p>
              </Stack>
            )}
            <PrimaryActionButton
              text={t("retry")}
              disabled={countdownIsEnabled || loading}
              onClick={handleRetryLocationCheck}
            />
          </Stack>
        </div>
      </BottomDrawer>

      {loading && <VerifyingLocationOverlay />}
    </>
  );
}
